import React, { useContext, useEffect, useState } from 'react';
import UserContext from '../../context/UserContext.js';
import { NombreEquipoCategoria } from '../funciones/FuncionesEquipo.js';
import { NombreApellidoGol } from '../funciones/FuncionesPersona.js';
import styles from './FichaCuadroResultado.module.css';
import LinkPlus from '../UI/LinkPlus.js';
import BotonEdicion from '../UI/botones/BotonEdicion.js';
import BotonAceptar from '../UI/botones/BotonAceptar.js';
import MensajeError from '../UI/MensajeError.js';
import MensajeGrabando from '../UI/MensajeGrabando.js';
import * as ModoEdicion from '../constantes/ModoEdicion.js';
import * as Data from '../data/DataPartidos.js';
import { calcularResultadosPartido } from '../funciones/FuncionesResultadoPartido.js';
import ResultadosSecundarios from './ResultadosSecundarios.js';

const goleadoresPorEquipo = (goles, idEquipo) => {
  const goleadores = new Map();

  (goles || [])
    .filter((gol) => Number(gol.equipo && gol.equipo.id) === Number(idEquipo))
    .forEach((gol) => {
      const nombre = NombreApellidoGol(gol);
      if (!nombre) return;

      const idPersona = gol.jugador?.id ?? gol.formacionJugador?.persona?.id;
      const identidad =
        (idPersona != null && `persona-${idPersona}`) ||
        (gol.formacionJugador?.idPartidoFormacion != null &&
          `formacion-${gol.formacionJugador.idPartidoFormacion}`) ||
        (gol.jugadorSinId && `sin-id-${gol.jugadorSinId.apellido}-${gol.jugadorSinId.nombre}`) ||
        `nombre-${nombre}`;
      const goleador = goleadores.get(identidad) || {
        identidad,
        nombre,
        cantidad: 0,
        penales: 0,
        enContra: false,
        primerGol: goleadores.size,
      };

      goleador.cantidad += 1;
      if (/penal/i.test(gol.tipoConversion && gol.tipoConversion.descripcion)) {
        goleador.penales += 1;
      }
      goleador.enContra = goleador.enContra || Boolean(gol.tipoConversion?.enContra);
      goleadores.set(identidad, goleador);
    });

  return Array.from(goleadores.values()).sort(
    (a, b) => Number(b.cantidad > 1) - Number(a.cantidad > 1) || a.primerGol - b.primerGol
  );
};

const textoGoleador = ({ nombre, cantidad, penales, enContra }) => {
  const anotacionNombre = enContra ? ' (e/c)' : '';
  let anotacionGoles = '';
  if (cantidad > 1) {
    anotacionGoles = ` (${cantidad}${penales > 0 ? `, ${penales}p` : ''})`;
  } else if (penales > 0) {
    anotacionGoles = ' (p)';
  }
  return `${nombre}${anotacionNombre}${anotacionGoles}`;
};

const ListaGoleadores = ({ goles, equipo, estilo, etiqueta }) => {
  const goleadores = goleadoresPorEquipo(goles, equipo && equipo.id);

  return (
    <div className={estilo} aria-label={etiqueta}>
      {goleadores.map((goleador, index) => (
        <React.Fragment key={goleador.identidad}>
          {index > 0 ? ', ' : ''}
          {textoGoleador(goleador)}
        </React.Fragment>
      ))}
    </div>
  );
};

const FichaCuadroResultado = (props) => {
  const partido = props.partido;
  const goles = props.goles;
  const { usuario } = useContext(UserContext);
  const puedeEditar = Boolean(usuario && usuario.puedeEditar);
  const [editarPrincipal, setEditarPrincipal] = useState(false);
  const [golesPrincipales, setGolesPrincipales] = useState({
    golesLocal: partido.golesLocal ?? null,
    golesVisitante: partido.golesVisitante ?? null,
  });
  const [guardando, setGuardando] = useState(false);
  const [errores, setErrores] = useState(null);

  useEffect(() => {
    setGolesPrincipales({
      golesLocal: partido.golesLocal ?? null,
      golesVisitante: partido.golesVisitante ?? null,
    });
  }, [partido.golesLocal, partido.golesVisitante]);

  const guardarResultadoPrincipal = async () => {
    setErrores(null);
    setGuardando(true);
    try {
      await Data.PatchPartido(partido.id, golesPrincipales);
      const actualizado = await Data.FetchPartido(partido.id);
      // El backend actualiza resultadoLocal/resultadoVisitante en la base; acá solo se
      // refleja el cálculo en pantalla sin una carga adicional del partido.
      const resultadosCalculados = calcularResultadosPartido(
        golesPrincipales.golesLocal,
        golesPrincipales.golesVisitante,
        props.resultados || []
      );
      props.onPartidoActualizado(
        resultadosCalculados ? { ...actualizado, ...resultadosCalculados } : actualizado
      );
      setEditarPrincipal(false);
    } catch (error) {
      setErrores([error.message || 'No se pudo guardar el resultado']);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <React.Fragment>
      <div className={`${styles.resultadoPartido}`}>
        <div className={`${styles.filaResultado}`}>
          <div className={`${styles.nombreEquipo}`}>
            <LinkPlus
              className={`${styles.linkEquipo}`}
              to={'/equipo/' + partido.equipoLocal.id}
              adaptativo={true}
            >
              {partido.equipoLocal &&
                NombreEquipoCategoria(
                  partido.equipoLocal.nombre,
                  partido.equipoLocal.categoria,
                  partido.categoria
                )}
            </LinkPlus>
          </div>
          <div className={styles.resultadoEquipo}>
            {editarPrincipal ? (
              <input
                aria-label="Goles local"
                className={styles.golesPrincipales}
                type="text"
                inputMode="numeric"
                maxLength={2}
                value={golesPrincipales.golesLocal ?? ''}
                onChange={(event) => {
                  const texto = event.target.value.replace(/\D/g, '').slice(0, 2);
                  setGolesPrincipales((actual) => ({
                    ...actual,
                    golesLocal: texto === '' ? null : Number(texto),
                  }));
                }}
              />
            ) : (
              partido.resultadoLocal
            )}
          </div>
          <span className={styles.separadorResultado}>-</span>
          <div className={styles.resultadoEquipo}>
            {editarPrincipal ? (
              <input
                aria-label="Goles visitante"
                className={styles.golesPrincipales}
                type="text"
                inputMode="numeric"
                maxLength={2}
                value={golesPrincipales.golesVisitante ?? ''}
                onChange={(event) => {
                  const texto = event.target.value.replace(/\D/g, '').slice(0, 2);
                  setGolesPrincipales((actual) => ({
                    ...actual,
                    golesVisitante: texto === '' ? null : Number(texto),
                  }));
                }}
              />
            ) : (
              partido.resultadoVisitante
            )}
          </div>
          <div className={`${styles.nombreEquipo}`}>
            <LinkPlus
              className={`${styles.linkEquipo}`}
              to={'/equipo/' + partido.equipoVisitante.id}
              adaptativo={true}
            >
              {partido.equipoVisitante &&
                NombreEquipoCategoria(
                  partido.equipoVisitante.nombre,
                  partido.equipoVisitante.categoria,
                  partido.categoria
                )}
            </LinkPlus>
          </div>
        </div>
        {puedeEditar && (
          <div className={styles.accionesResultadoPrincipal}>
            <div className={styles.botonesResultadoPrincipal}>
              {editarPrincipal ? (
                <React.Fragment>
                  <BotonAceptar onClick={guardarResultadoPrincipal} />
                  <BotonEdicion
                    modo={ModoEdicion.CANCELAR}
                    onClick={() => {
                      setGolesPrincipales({
                        golesLocal: partido.golesLocal ?? null,
                        golesVisitante: partido.golesVisitante ?? null,
                      });
                      setEditarPrincipal(false);
                    }}
                  />
                </React.Fragment>
              ) : (
                <BotonEdicion modo={ModoEdicion.EDITAR} onClick={() => setEditarPrincipal(true)} />
              )}
            </div>
          </div>
        )}
        <ResultadosSecundarios
          idPartido={partido.id}
          resultados={props.resultados || []}
          partido={partido}
          onResultadosActualizados={props.onResultadosActualizados}
          onPartidoActualizado={props.onPartidoActualizado}
        />
        <MensajeGrabando isGrabando={guardando} />
        {errores && <MensajeError errores={errores} />}
        <div className={`${styles.filaGoles}`}>
          <ListaGoleadores
            goles={goles}
            equipo={partido.equipoLocal}
            estilo={styles.goleadoresLocal}
            etiqueta="Goleadores del equipo local"
          />
          <ListaGoleadores
            goles={goles}
            equipo={partido.equipoVisitante}
            estilo={styles.goleadoresVisitante}
            etiqueta="Goleadores del equipo visitante"
          />
        </div>
      </div>
    </React.Fragment>
  );
};

export default FichaCuadroResultado;
